import React from 'react';
import { Chip, Skeleton, Tooltip } from '@mui/material';
import CircleIcon from '@mui/icons-material/Circle';

import useCurrentSite from './hooks/useCurrentSite';
import { useDashboardTokens } from './tokens';

/**
 * 현재 company 환경에서 구동 중인 사이트(banpo/central/osong)를 표시하는 chip.
 *
 *  - running=true & site present → green dot + 사이트명
 *  - running=false but site present → amber dot + "{site} (stopped)"
 *  - 컨테이너 없음 (site="" & running=false) → grey dot + "구동 사이트 없음"
 *  - 조회 실패 → red dot + "조회 실패"
 *
 * 30초 폴링(useCurrentSite). 동일 호스트에 site 컨테이너는 정확히 1개라
 * 사이트 교체 시 30초 이내 자동 갱신.
 */
const CurrentSiteChip: React.FC = () => {
  const tokens = useDashboardTokens();
  const { data, loading, error } = useCurrentSite();

  if (loading) {
    return <Skeleton variant="rounded" width={140} height={32} />;
  }

  const fail = !!error;
  const noContainer = !fail && !data?.site && !data?.running;
  const stopped = !fail && !!data?.site && !data?.running;
  const ok = !fail && !!data?.site && !!data?.running;

  let label = '조회 실패';
  let color: string = tokens.colors.muted;
  let tooltip = error ?? '';

  if (ok && data) {
    label = `구동: ${data.site}`;
    color = '#22c55e'; // green-500
    tooltip = `${data.container_name} (${data.source})`;
  } else if (stopped && data) {
    label = `${data.site} (stopped)`;
    color = '#f59e0b'; // amber-500
    tooltip = `${data.container_name} 컨테이너가 정지 상태`;
  } else if (noContainer) {
    label = '구동 사이트 없음';
    color = '#9ca3af'; // gray-400
    tooltip = 'router 컨테이너 미존재 — 사이트가 띄워지지 않음';
  } else if (fail) {
    color = '#ef4444'; // red-500
  }

  return (
    <Tooltip title={tooltip} arrow placement="bottom">
      <Chip
        size="small"
        icon={<CircleIcon sx={{ color: `${color} !important`, fontSize: 10 }} />}
        label={label}
        sx={{
          bgcolor: tokens.colors.surface,
          border: `1px solid ${tokens.colors.border}`,
          color: tokens.colors.text,
          fontWeight: 600,
          letterSpacing: 0.2,
          height: 32,
        }}
        data-testid="current-site-chip"
      />
    </Tooltip>
  );
};

export default CurrentSiteChip;
